import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export interface CardGridSkeletonProps {
  /** How many placeholder cards to render. Defaults to 3. */
  count?: number;
  /** Accessible label announced by screen readers. */
  label?: string;
  /**
   * Tailwind grid classes for the wrapping container. Override to match the
   * real grid that will replace the skeleton (so the layout doesn't shift).
   */
  gridClassName?: string;
  /** Minimum card height — keep aligned with the real card to avoid shifts. */
  minCardHeight?: string;
}

/**
 * Reusable card-grid skeleton with consistent ARIA semantics.
 *
 * Use this anywhere we render a grid of fetched cards (landing certifications,
 * chapters, etc.). It announces a polite "loading" state to assistive tech
 * and stays mounted for the full duration of the fetch — including retries —
 * so users never see a misleading empty state.
 */
export const CardGridSkeleton = ({
  count = 3,
  label = "Loading content",
  gridClassName = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6",
  minCardHeight = "min-h-[220px]",
}: CardGridSkeletonProps) => {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
      className={gridClassName}
    >
      {Array.from({ length: count }).map((_, i) => (
        <Card
          key={i}
          aria-hidden="true"
          className="border-border shadow-sm overflow-hidden"
        >
          <CardContent className={`pt-8 pb-6 px-6 flex flex-col ${minCardHeight}`}>
            <Skeleton className="w-12 h-12 rounded-lg mb-5" />
            <Skeleton className="h-5 w-3/4 mb-2" />
            <Skeleton className="h-3 w-1/2 mb-4" />
            <Skeleton className="h-3 w-full mb-2" />
            <Skeleton className="h-3 w-5/6 mb-2" />
            <Skeleton className="h-3 w-2/3" />
          </CardContent>
        </Card>
      ))}
      <span className="sr-only">{label}…</span>
    </div>
  );
};

export default CardGridSkeleton;